import { useEffect, useState, useCallback } from "react";

import type { Dispatch, SetStateAction } from "react";

function usePersistentState<T>(key: string, initialValue: T): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState<T>(() => {
    try {
      const storedValue = localStorage.getItem(key);
      if (storedValue !== null) {
        return JSON.parse(storedValue) as T;
      }
      return initialValue;
    } catch (_e) {
      return initialValue;
    }
  });

  const setPersistentState = useCallback(
    (value: SetStateAction<T>) => {
      localStorage.setItem(key, JSON.stringify(value));

      // window.addEventListener("storage") does not capture localStorage changes in the same tab,
      // so we dispatch a custom event to notify ourselves of the change.

      const event = new CustomEvent("local-persistent-state", { detail: { key, value } });
      window.dispatchEvent(event);
    },
    [key],
  );

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key && event.newValue !== null) {
        setState(JSON.parse(event.newValue) as T);
      }
    };
    const handleLocalStorageChange = (event: CustomEvent<{ key: string; value: T }>) => {
      if (event.detail.key === key) {
        setState(event.detail.value);
      }
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("local-persistent-state", handleLocalStorageChange as EventListener);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener(
        "local-persistent-state",
        handleLocalStorageChange as EventListener,
      );
    };
  }, [key]);

  return [state, setPersistentState];
}

export default usePersistentState;
